import { isDevelopment } from "@/common/env";
import { PublicClientApplication } from "@azure/msal-browser";

const baseURL = process.env.BASE_URL;

if (!baseURL) {
  throw new Error("Base URL not defined in environment variables");
}

const msalConfig = {
  auth: {
    clientId: "c8418491-7526-448e-8cab-aa635fceec48",
    authority:
      "https://login.microsoftonline.com/372ee9e0-9ce0-4033-a64a-c07073a91ecd",
    redirectUri: `${baseURL}login`,
    
  },
  cache: {
    cacheLocation: "sessionStorage",
  },
  system: {
    tokenRenewalOffsetSeconds: 600,
    iframeHashTimeout: 30000, // iframe is used to refresh the refresh_token as it has a 24h lifetime
  },
};

export enum LogStatus {
  None,
  NotAuthorized,
  NoAccess,
  Logged,
}

export class Authorization {
  private static readonly msalScopes = [
    "api://c8418491-7526-448e-8cab-aa635fceec48/access_as_user",
  ];

  private static readonly tokenRefreshBufferSeconds = 60;

  private static _token: string = "";
  private static _name: string = "Unknown user";
  private static _logStatus: LogStatus = LogStatus.None;
  private static _id: string = "";
  private static _redirectInProgress: boolean = false;

  static tokenLooksExpired(): boolean {
    if (!this._token) {
      return true;
    }

    try {
      const payloadBase64 = this._token.split(".")[1];
      if (!payloadBase64) {
        return true;
      }

      const payload = JSON.parse(atob(payloadBase64.replace(/-/g, "+").replace(/_/g, "/")));
      const exp = Number(payload?.exp ?? 0);
      if (!exp) {
        return true;
      }

      const expiresAt = exp * 1000;
      const refreshThreshold = this.tokenRefreshBufferSeconds * 1000;
      return Date.now() >= expiresAt - refreshThreshold;
    } catch {
      return true;
    }
  }

  static async refreshTokenIfExpired(): Promise<boolean> {
    if (!this.tokenLooksExpired()) {
      return true;
    }

    try {
      await this.getToken();
      return this.isLogged && !!this._token;
    } catch (error) {
      console.error("Background token refresh failed", error);
      return false;
    }
  }

  static get token(): string {
    return this._token;
  }

  static set token(value: string) {
    this._token = value;
  }

  static get accountName(): string {
    return this._name;
  }

  static get id(): string {
    return this._id;
  }

  static get logStatus(): LogStatus {
    return this._logStatus;
  }

  static get isLogged(): boolean {
    return this._logStatus == LogStatus.Logged;
  }

  private static msalInstance = new PublicClientApplication(msalConfig);

  static async signIn() {
      await this.getToken();
  }

  static async reLogIn(): Promise<boolean> {
    try {
      await this.getToken();
      return this.isLogged;
    } catch (error) {
      console.error("Authorization re-login failed", error);
      this._logStatus = LogStatus.NotAuthorized;
      return false;
    }
  }

  static async getToken() {
    await this.msalInstance.initialize();
    
    // Handle redirect response from Azure login
    const redirectResponse = await this.msalInstance.handleRedirectPromise();
    if (redirectResponse?.account) {
      this.token = redirectResponse.accessToken;
      this._logStatus = LogStatus.Logged;
      this._name = redirectResponse.account.name ?? "";
      this._id = redirectResponse.account.localAccountId ?? "";
      this._redirectInProgress = false;
      return;
    }

    const accounts = this.msalInstance.getAllAccounts();
    if (!accounts.length) {
      // Only redirect once - if already in progress, don't try again
      if (this._redirectInProgress) {
        console.log("Redirect already in progress, waiting...");
        return;
      }
      
      // No cached accounts - initiate redirect to Azure login
      try {
        console.log("No cached accounts, redirecting to Azure login");
        this._redirectInProgress = true;
        await this.msalInstance.loginRedirect({
          scopes: this.msalScopes,
        });
      } catch (e) {
        console.error("Redirect to login failed", e);
        this._logStatus = LogStatus.NotAuthorized;
        this._redirectInProgress = false;
      }
      return;
    }

    // Try to get token silently with cached account
    const silentRequest = {
      scopes: this.msalScopes,
      account: accounts[0],
    };

    try {
      const response = await this.msalInstance.acquireTokenSilent(silentRequest);
      this.token = response.accessToken;
      this._logStatus = LogStatus.Logged;
      this._name = response.account?.name ?? "";
      this._id = response.account?.localAccountId ?? "";
    } catch (e) {
      console.error("Silent token acquisition failed", e);
      if (!this._redirectInProgress) {
        try {
          console.log("Silent token failed, redirecting to Azure login");
          this._redirectInProgress = true;
          await this.msalInstance.loginRedirect({
            scopes: this.msalScopes,
          });
        } catch (e) {
          console.error("Redirect to login failed", e);
          this._logStatus = LogStatus.NotAuthorized;
          this._redirectInProgress = false;
        }
      }
    }
  }
}
